import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Box, Button, Checkbox, Flex, FormControl, FormLabel, Heading, Input, Select, Text, useColorModeValue } from "@chakra-ui/react";
import { useDropzone } from "react-dropzone";
import { useLocation } from "react-router-dom";
import BackButton from "components/BackButton";
import { getUserData } from "utils";
import { createAPIEndPoint } from "config/api/api";
import toast from "react-hot-toast";

const ServiceForm = () => {
  const textColor = useColorModeValue('navy.700', 'white');

  const location = useLocation();
  const { type, serviceId } = location.state || {};

  const userData = getUserData();
  const clinic_id = userData.clinic_id ?? null;
  console.log("ServiceForm ~ serviceId:", serviceId)
  const userId = userData?.id ?? null;


  const [initialData, setInitialData] = useState([]);
  console.log("ServiceForm ~ initialData:", initialData)

  const fetchService = async () => {
    try {
      const response = await createAPIEndPoint('services/').fetchById(serviceId);
      const service = response.data.service || {};
      setInitialData(service || []);
      const selectedAreas = [service.treatment_area_id, service.treatment_area2_id].filter(Boolean);
      setSelectedTreatmentAreas(selectedAreas);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Error fetching service');
    }
  };


  useEffect(() => {
    if (initialData.id) {
      setSelectedTreatmentAreas([
        initialData.treatment_area_id,
        initialData.treatment_area2_id
      ].filter(Boolean));
    }
  }, [initialData]);


  const [categories, setCategories] = useState([]);

  const fetchCategories = async () => {
    try {
      const response = await createAPIEndPoint(`service_categories/get_all/${clinic_id}`).fetchAll();
      setCategories(response.data.service_categories || []);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Error fetching service categories');
    }
  };


  const fetchTreatmentAreas = async () => {
    try {
      const response = await createAPIEndPoint(`treatment_areas/get_all/${clinic_id}`).fetchAll();
      setTreatmentAreas(response.data.treatment_areas || []);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Error fetching treatment areas');
    }
  };

  useEffect(() => {
    fetchCategories()
    fetchTreatmentAreas();
  }, []);

  useEffect(() => {
    if (serviceId) {
      fetchService();
    }
  }, [serviceId]);



  const [treatmentAreas, setTreatmentAreas] = useState([]);
  const [selectedTreatmentAreas, setSelectedTreatmentAreas] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleTreatmentChange = (id) => {
    setSelectedTreatmentAreas((prev) => {
      if (prev.includes(id)) {
        return prev.filter((areaId) => areaId !== id); // Remove if already selected
      }
      if (prev.length < 2) {
        return [...prev, id]; // Add if less than 2 selected
      }
      toast.error("You can only select up to 2 treatment areas.");
      return prev;
    });
  };



  const handleCreate = async (values, { resetForm }) => {

    const payload = {
      clinic_id,
      category_id: values.category,
      user_id: userId,
      treatment_area_id: selectedTreatmentAreas[0],
      treatment_area2_id: selectedTreatmentAreas[1],
      code: values.code,
      description: values.description,
      office_code: values.officeCode,
      amount_il: values.amountIL,
      amount_wi: values.amountWI,
    };

    try {
      setLoading(true)
      if (serviceId) {
        await createAPIEndPoint('services/').update(serviceId, payload);
        toast.success('Service updated successfully!');
      } else {
        await createAPIEndPoint('services').create(payload);
        toast.success('Service added successfully!');
        resetForm();
        setInitialData([])
        setTreatmentAreas([])
        setSelectedTreatmentAreas([])

      }
      setLoading(false)
    } catch (error) {
      setLoading(false)
      toast.error(error?.response?.data?.error || 'An error occurred while processing the service.');
    }
  };





  // const [image, setImage] = useState(null);
  // useEffect(() => {
  //   if (type === "edit" && initialData.image) {
  //     setImage(initialData.image);
  //   }
  // }, [type, initialData]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      code: initialData.code || "",
      description: initialData.description || "",
      officeCode: initialData.office_code || "",
      category: initialData.category_id || "",
      amountIL: initialData.amount_il || "",
      amountWI: initialData.amount_wi || "",
    },
    validationSchema: Yup.object({
      code: Yup.string().required("Code is required"),
      description: Yup.string().required("Description is required"),
      officeCode: Yup.string().required("Office Code is required"),
      amountIL: Yup.number().required("Amount IL is required"),
      amountWI: Yup.number().required("Amount WI is required"),
    }),
    onSubmit: handleCreate,
  });




  // const { getRootProps, getInputProps } = useDropzone({
  //   accept: "image/*",
  //   onDrop: (acceptedFiles) => {
  //     setImage(URL.createObjectURL(acceptedFiles[0]));
  //   },
  // });

  return (
    <Box pt={{ base: "80px", md: "80px", xl: "80px" }}>
      <BackButton />
      <Flex align="center" justify="center">
        <Box p={6} borderRadius="lg" boxShadow="lg" bg="white" w="500px">
          <Heading color={textColor} fontSize={{ base: "24px", md: "28px", lg: "32px" }} mb="10px">
            {type === "edit" ? "Edit Service" : "Add Service"}
          </Heading>

          {/* Drag & Drop Image Upload */}
          {/* <FormControl mb={2}>
            <FormLabel>Upload Image</FormLabel>
            <Box {...getRootProps()} px={4} py={6} borderRadius={4} border="2px dashed #E4E9EF" color="CaptionText" cursor="pointer" textAlign="center">
              <input {...getInputProps()} />
              {image ? <img src={image} alt="Uploaded" style={{ width: "100%", height: "auto" }} /> : "Drag & drop an image or click to select"}
            </Box>
          </FormControl> */}

          {/* Other Form Fields */}
          {['code', 'description', 'officeCode'].map((field) => (
            <FormControl key={field} isInvalid={formik.touched[field] && formik.errors[field]} mb={2}>
              <FormLabel textTransform="capitalize">{field.replace(/([A-Z])/g, " $1")}</FormLabel>
              <Input variant="auth"
                fontSize="sm"
                fontWeight="500" {...formik.getFieldProps(field)} placeholder={`Enter ${field}`} />
              {formik.touched[field] && formik.errors[field] && (
                <Text color="red.500" fontSize="sm">{formik.errors[field]}</Text>
              )}
            </FormControl>
          ))}

          {/* Category Select */}
          <FormControl mb={2}>
            <FormLabel>Category</FormLabel>
            <Select {...formik.getFieldProps("category")}>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.category}
                </option>
              ))}
            </Select>
          </FormControl>

          {/* Treatment Area Checkboxes */}
          <FormControl mb={2}>
            <FormLabel>Treatment Area</FormLabel>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
              {treatmentAreas.map((area) => (
                <Checkbox
                  key={area.id}
                  isChecked={selectedTreatmentAreas.includes(area.id)}
                  onChange={() => handleTreatmentChange(area.id)}
                  mr={2}
                >
                  <span style={{ fontSize: "14px" }}>
                    {area.treatment_area}
                  </span>
                </Checkbox>
              ))}
            </Box>
          </FormControl>


          {/* Amount Fields */}
          {['amountIL', 'amountWI'].map((field) => (
            <FormControl key={field} isInvalid={formik.touched[field] && formik.errors[field]} >
              <FormLabel textTransform="capitalize">{field.replace(/([A-Z])/g, " $1")}</FormLabel>
              <Input {...formik.getFieldProps(field)} type="number" placeholder={`Enter ${field}`} mb={2} />
              {formik.touched[field] && formik.errors[field] && (
                <Text color="red.500" fontSize="sm">{formik.errors[field]}</Text>
              )}
            </FormControl>
          ))}

          {/* Submit Button */}
          <Button type="submit" colorScheme="blue" w="100%" mt={4} onClick={formik.handleSubmit} isLoading={loading}>
            {type === "edit" ? "Update Service" : "Submit"}
          </Button>
        </Box>
      </Flex>
    </Box>
  );
};

export default ServiceForm;
